import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ------------------------------------------------------------------
// Transient-error retry interceptor (installed once, applies to every
// axios call across the app). When the backend is briefly overloaded
// (e.g. mid-way through a big scrape/verification job) a single request
// can come back 5xx or a network error and we'd show "Failed to load".
// One automatic retry with 800ms backoff makes those user-facing errors
// disappear without the user noticing.
// Notably we do NOT retry 4xx — those are intentional client errors
// (auth, validation, not-found) that should fail fast.
// ------------------------------------------------------------------
let __retryInstalled = false;
function installAxiosRetry() {
  if (__retryInstalled) return;
  __retryInstalled = true;
  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const config = error.config || {};
      const status = error.response?.status;
      const isNetwork = !error.response;  // ECONNRESET / DNS / offline
      const is5xx = status >= 500 && status <= 599;
      const retriable = isNetwork || is5xx;
      // GET is idempotent — safe to retry. POST/PUT/DELETE we skip to avoid
      // duplicate creates (the user can manually retry those).
      const method = (config.method || "get").toLowerCase();
      const idempotent = method === "get" || method === "head" || method === "options";
      if (!retriable || !idempotent || config.__retried) {
        return Promise.reject(error);
      }
      config.__retried = true;
      await new Promise((r) => setTimeout(r, 800));
      return axios(config);
    },
  );
}
installAxiosRetry();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [activeOrgId, setActiveOrgIdState] = useState(localStorage.getItem("activeOrgId") || "");
  const [loading, setLoading] = useState(true);

  // Cross-org super admins: role-based, with bootstrap email fallback
  const isSuperAdmin = !!user && (
    user.role === "super_admin" ||
    (user.role === "admin" && (user.email || "").toLowerCase() === "info@dolphin-digital.com")
  );

  // Org admin: can manage their own org's keys and users
  const isOrgAdmin = !!user && (user.role === "admin" || user.role === "super_admin");

  // Orgs the user has access to. Super admin can reach any org; everyone else is
  // limited to `organization_ids` on their user doc (home org always included).
  const userOrgIds = user?.organization_ids && user.organization_ids.length > 0
    ? user.organization_ids
    : (user?.organization_id ? [user.organization_id] : []);
  const canSwitchOrgs = isSuperAdmin || userOrgIds.length > 1;

  // Effective org id powering the X-Active-Org header. Empty string = "All Organizations"
  // aggregated view (super admin only). Any other value = scoped to that single org.
  const effectiveOrgId = canSwitchOrgs ? activeOrgId : (user?.organization_id || "");
  // Override badge: true whenever the user is NOT viewing their default home org.
  // Covers both "All Organizations" (empty activeOrgId) and other specific orgs.
  const isOrgOverrideActive = canSwitchOrgs && activeOrgId !== (user?.organization_id || "");

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem("token");
      if (savedToken) {
        try {
          const headers = { Authorization: `Bearer ${savedToken}` };
          // Auth/me must NOT use the override (we want the user's own home-org info)
          const response = await axios.get(`${API}/auth/me`, { headers });
          setUser(response.data);
          setToken(savedToken);
          // Back-compat: if we never stored an activeOrgId (old session before
          // home-org-is-default was introduced), default it to the home org now.
          if (!localStorage.getItem("activeOrgId") && response.data?.organization_id) {
            localStorage.setItem("activeOrgId", response.data.organization_id);
            setActiveOrgIdState(response.data.organization_id);
          }
        } catch (error) {
          // Only wipe the session if the server explicitly tells us the token
          // is bad (401/403). For transient issues — network blip, backend
          // restart, 5xx, CORS hiccup — we keep the token and let the user
          // try again. Previously ANY failure here logged users out at random.
          const status = error?.response?.status;
          const isAuthFailure = status === 401 || status === 403;
          if (isAuthFailure) {
            localStorage.removeItem("token");
            localStorage.removeItem("activeOrgId");
            setToken(null);
            setUser(null);
            setActiveOrgIdState("");
          } else {
            // Keep the token in localStorage; surface a soft state by leaving
            // user null. ProtectedRoute will redirect to /login this once, but
            // because the token is preserved a refresh after the backend
            // recovers will re-auth automatically.
            console.warn("Auth probe failed (transient, keeping token):", status, error?.message);
            setToken(savedToken);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    const response = await axios.post(`${API}/auth/login`, { email: normalizedEmail, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    // Default view scope = user's home org (so super admins don't accidentally
    // see cross-org data by default). To view all orgs aggregated, they must
    // explicitly select "All Organizations" from the org switcher.
    const defaultOrgId = userData?.organization_id || "";
    if (defaultOrgId) {
      localStorage.setItem("activeOrgId", defaultOrgId);
    } else {
      localStorage.removeItem("activeOrgId");
    }
    setToken(newToken);
    setUser(userData);
    setActiveOrgIdState(defaultOrgId);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("activeOrgId");
    setToken(null);
    setUser(null);
    setActiveOrgIdState("");
  };

  const setActiveOrgId = (orgId) => {
    if (!canSwitchOrgs) return;
    // Empty string = "All Organizations" aggregated view (super admin only).
    // Any org id (including the user's home org) = explicit single-org scope.
    if (!orgId) {
      localStorage.removeItem("activeOrgId");
      setActiveOrgIdState("");
    } else {
      localStorage.setItem("activeOrgId", orgId);
      setActiveOrgIdState(orgId);
    }
    // Force a hard refresh so every page re-fetches with the new effective org
    window.location.reload();
  };

  const getAuthHeaders = () => {
    const headers = { Authorization: `Bearer ${token}` };
    if (canSwitchOrgs && activeOrgId) {
      headers["X-Active-Org"] = activeOrgId;
    }
    return headers;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        getAuthHeaders,
        isSuperAdmin,
        isOrgAdmin,
        activeOrgId,
        setActiveOrgId,
        effectiveOrgId,
        isOrgOverrideActive,
        userOrgIds,
        canSwitchOrgs,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
